import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';

export interface Organization {
  id: string;
  name: string;
  role: string;
  subscription?: string;
  category?: string;
  logoFile?: string;
  logo_url?: string;
  timezone?: string;
  createdAt?: string;
  membershipCreatedAt?: string;
  /** 'staff' | 'citizen' — 'staff' also covers legacy rows with no member_type. */
  memberType?: string;
  /**
     True when the user holds at least one NON-citizen membership here. Every
     Pro user also has a citizen membership (auth.js creates one at sign-up),
     so membership alone never means "this is a place the user works".
  */
  isProMembership?: boolean;
}

/*
  A user can hold several memberships in one org (the DB's unique index
  includes member_type), so /organizations/user can return the same org once
  per membership. Collapse to one entry carrying the highest-privilege role —
  duplicates broke the sidebar list (same org twice, duplicate React keys),
  and every role check works on the best role anyway.
*/
const ROLE_PRIORITY: Record<string, number> = {
  system_admin: 0,
  org_admin: 1,
  contributor: 2,
  member: 3,
  guest: 4,
};

const rolePriority = (role: string | undefined): number =>
  ROLE_PRIORITY[(role ?? '').toLowerCase()] ?? 5;

function collapseMemberships(orgs: Organization[]): Organization[] {
  const byId = new Map<string, Organization>();
  for (const org of orgs) {
    const existing = byId.get(org.id);
    const isPro = (org.memberType ?? 'staff') !== 'citizen';
    /*
      Pro-ness ORs across the collapsed rows rather than following the winning
      role: an org-signup link gives a user a citizen membership in a Pro org
      too, so the same org can arrive twice with different member types. One
      staff row anywhere makes it a place the user works.
    */
    const merged: Organization = {
      ...(!existing || rolePriority(org.role) < rolePriority(existing.role) ? org : existing),
      isProMembership: isPro || existing?.isProMembership === true,
    };
    byId.set(org.id, merged);
  }
  return Array.from(byId.values());
}

export const useOrganizations = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/organizations/user');

      if (response.data.success) {
        const orgs = response.data.data.organizations.map((org: any) => ({
          id: org.id,
          name: org.name,
          role: org.role,
          subscription: org.subscription,
          category: org.category,
          logoFile: org.logoFile,
          logo_url: org.logo_url,
          timezone: org.timezone,
          createdAt: org.createdAt,
          membershipCreatedAt: org.membershipCreatedAt,
          memberType: org.memberType
        }));

        setOrganizations(collapseMemberships(orgs));
      } else {
        setError('Failed to fetch organizations');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch organizations');
      // Fallback to empty array if there's an error
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  /*
    The orgs the user actually WORKS in. Pro surfaces want this, never the raw
    list: the citizen membership is not a working context, and because
    GET /organizations/user orders by name it can easily sort first. Anything
    org-scoped that picks implicitly should pick from here — and where a
    workspace is in scope, derive the org from its owner instead of picking at
    all (see services/billCustomFieldDefs.ts).
  */
  const proOrganizations = useMemo(
    () => organizations.filter((org) => org.isProMembership !== false),
    [organizations]
  );

  return { organizations, proOrganizations, loading, error, refetch: fetchOrganizations };
};

export const useUserAccess = () => {
  const [hasSubscriptionAccess, setHasSubscriptionAccess] = useState(true); // Default to true during loading
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserAccess = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if token exists
        const token = localStorage.getItem('token');

        if (!token) {
          setError('No authentication token found');
          setHasSubscriptionAccess(false);
          setLoading(false);
          return;
        }

        const response = await api.get('/auth/me');

        if (response.data.success) {
          setHasSubscriptionAccess(response.data.data.hasSubscriptionAccess ?? true);
        } else {
          setError('Failed to fetch user access');
          setHasSubscriptionAccess(true); // Fail open for safety
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch user access');
        setHasSubscriptionAccess(true); // Fail open for safety
      } finally {
        setLoading(false);
      }
    };

    fetchUserAccess();
  }, []);

  return { hasSubscriptionAccess, loading, error };
}; 